<script setup>
import Buttons from '@/components/ui/Buttons.vue';
defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
const formatPrice = (price) => {
  return price.toLocaleString('uk-UA');
};
</script>
<template>
  <section
    class="section section--car-hero"
    :class="[data?.background_color ? `bg-${data.background_color}` : '']"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div class="car-hero">
        <div
          v-if="data?.image && data?.image?.src"
          class="car-hero__image"
          data-aos="fade-left"
        >
          <picture v-if="data?.image?.src">
            <source
              v-if="data?.image?.src_mobile"
              media="(max-width: 767px)"
              :srcset="data?.image?.src_mobile"
            />
            <source
              v-if="data?.image?.src_tablet"
              media="(min-width: 768px) and (max-width: 1023px)"
              :srcset="data?.image?.src_tablet"
            />
            <NuxtImg
              :src="data?.image?.src"
              :alt="data?.image?.alt"
              :height="data?.image?.height"
              :width="data?.image?.width"
              preload
              fetchpriority="high"
              loading="eager"
            />
          </picture>
        </div>
        <div class="car-hero__content">
          <h1
            v-if="data?.title"
            class="mb-0 car-hero__title h1"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            v-html="data?.title"
          ></h1>
          <div class="car-hero__info">
            <div class="car-hero__data">
              <div
                class="car-hero__data-top"
                data-aos="fade-up"
                data-aos-easing="ease-out-cubic"
              >
                <div v-if="data?.in_stock" class="car-hero__instock">
                  <CIcon
                    name="check"
                    width="12"
                    height="12"
                    class="car-hero__instock-icon"
                  />
                  <span class="car-hero__instock-text body-3-medium">{{
                    $t('car.available')
                  }}</span>
                </div>
                <div class="car-hero__price">
                  <div class="car-hero__price-item">
                    <span class="car-hero__price-current h6">
                      <template v-if="data?.pricing?.price_current">
                        <template v-if="!data?.pricing?.discount">{{
                          $t('car.from')
                        }}</template>
                        {{ formatPrice(data?.pricing?.price_current) }}
                        {{ data?.pricing?.currency }}
                      </template>
                      <template v-else>{{ $t('car.price_empty') }}</template>
                    </span>
                  </div>
                  <div
                    v-if="data?.pricing?.discount"
                    class="car-hero__price-item"
                  >
                    <span
                      v-if="data?.pricing?.price_old"
                      class="car-hero__price-original label"
                      >{{ formatPrice(data?.pricing?.price_old) }}
                      {{ data?.pricing?.currency }}</span
                    >
                    <span
                      v-if="data?.pricing?.discount"
                      class="car-hero__discount body-3-medium"
                      >-{{ data?.pricing?.discount }}%</span
                    >
                  </div>
                </div>
              </div>
              <div
                v-if="
                  (data?.financing?.monthly_payment &&
                    data?.financing?.max_payment) ||
                  kasko
                "
                class="car-hero__installments body-3-medium"
                data-aos="fade-up"
                data-aos-easing="ease-out-cubic"
              >
                <p
                  v-if="
                    data?.financing?.monthly_payment &&
                    data?.financing?.max_payment
                  "
                  class="mb-0 car-hero__installment"
                  :class="[
                    'car-hero__installment--with-tooltip' ??
                      data?.financing?.tooltip,
                  ]"
                >
                  <CIcon
                    class="car-hero__installment-icon"
                    name="credit"
                    width="12"
                    height="12"
                  />
                  <span>
                    {{
                      $t('car.payments_part1', {
                        payments: data?.financing?.max_payment,
                      })
                    }}
                    {{ formatPrice(data?.financing?.monthly_payment) }}
                    {{ $t('car.payments_part2') }}
                  </span>
                  <CIcon
                    v-if="data?.financing?.tooltip"
                    v-tooltip.top="{
                      value: data?.financing?.tooltip,
                      showDelay: 300,
                      hideDelay: 300,
                    }"
                    class="car-hero__installment-tooltip"
                    name="info"
                    width="12"
                    height="12"
                  />
                </p>
                <p
                  v-if="data?.financing?.kasko"
                  class="mb-0 car-hero__installment"
                >
                  <CIcon
                    class="car-hero__installment-icon"
                    name="warranty"
                    width="12"
                    height="12"
                  />
                  <span>
                    {{ $t('car.kasko', { kasko: data.financing.kasko }) }}
                  </span>
                </p>
              </div>
            </div>
            <Buttons
              v-if="data.in_stock"
              :template="'stock'"
              class="car-hero__buttons"
              data-aos="fade-up"
              data-aos-easing="ease-out-cubic"
              :buttons-prefix-class="'js-button-car-hero'"
              :buttons="[
                {
                  id: 1,
                  type: 'popup',
                  title: $t('car.get_consultation'),
                  popup: 'consultation',
                },
                {
                  id: 2,
                  type: 'popup',
                  title: $t('car.test_drive'),
                  popup: 'test_drive',
                },
                {
                  id: 3,
                  type: 'link',
                  title: $t('car.more_about_model'),
                  link: {
                    url: data.link_full,
                    target: '_self',
                    rel: '',
                    is_external: false,
                  },
                },
              ]"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.car-hero {
  display: flex;
  flex-direction: column;
  gap: 32px;
  text-align: center;
  @include media-breakpoint-up(sm) {
    gap: 48px;
    text-align: left;
  }
  @include media-breakpoint-up(md) {
    display: grid;
    gap: 16px;
    grid-template-columns: repeat(2, 1fr);
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 16);
  }
  &__image {
    @include media-breakpoint-up(md) {
      order: 2;
    }
    img {
      border-radius: 8px;
      width: 100%;
      height: auto;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-radius, 8);
      }
    }
  }
  &__content {
    display: flex;
    flex-direction: column;
    gap: 32px;
    @include media-breakpoint-up(sm) {
      gap: 48px;
    }
    @include media-breakpoint-up(md) {
      order: 1;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 48);
    }
  }
  &__title {
    margin: 0;
  }
  &__info {
    display: flex;
    flex-direction: column;
    gap: 32px;
    @include media-breakpoint-up(sm) {
      display: grid;
      gap: 16px;
      grid-template-columns: auto auto;
      justify-content: space-between;
    }
    @include media-breakpoint-up(md) {
      grid-template-columns: 1fr;
      gap: 48px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 48);
    }
  }
  &__data {
    display: flex;
    flex-direction: column;
    gap: 32px;
    @include media-breakpoint-up(sm) {
      gap: 48px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 48);
    }
    &-top {
      display: flex;
      flex-direction: column;
      gap: 16px;
      justify-content: start;
      @include media-breakpoint-up(sm) {
        display: grid;
        grid-template-columns: auto auto;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 16);
      }
    }
  }
  &__instock {
    align-items: center;
    border: 1px solid $divider;
    border-radius: 2px;
    display: inline-grid;
    grid-template-columns: 12px auto;
    gap: 8px;
    margin: 0 auto;
    padding: 3px 8px 2px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-width, 1);
      @include responsive-spacing(border-radius, 2);
      @include responsive-spacing(gap, 8);
      @include responsive-spacing(padding-top, 3);
      @include responsive-spacing(padding-bottom, 2);
      @include responsive-spacing(padding-left, 8);
      @include responsive-spacing(padding-right, 8);
      grid-template-columns: px-to-vw(12) auto;
    }
    &-icon {
      height: auto;
      width: 100%;
    }
  }
  &__price {
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: 16px;
    justify-content: center;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 16);
    }
    &-item {
      align-items: center;
      display: flex;
      flex-direction: row;
      gap: 8px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 8);
      }
    }
    &-original {
      color: $on-surface-variant-2;
      text-decoration: line-through;
    }
  }
  &__discount {
    border-radius: 2px;
    background-color: $primary;
    color: $white;
    padding: 5px 5px 1px 4px;
    position: relative;
    z-index: 1;
    &:before {
      background-color: inherit;
      content: '';
      height: 6px;
      left: -1px;
      position: absolute;
      top: calc(50% - 3px);
      transform: rotate(45deg);
      width: 6px;
      z-index: -1;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 2);
      @include responsive-spacing(padding-top, 5);
      @include responsive-spacing(padding-right, 5);
      @include responsive-spacing(padding-bottom, 1);
      @include responsive-spacing(padding-left, 4);
      &:before {
        @include responsive-spacing(height, 6);
        @include responsive-spacing(left, -1);
        @include responsive-spacing(width, 6);
        top: calc(50% - px-to-vw(3));
      }
    }
  }
  &__installments {
    display: flex;
    flex-direction: column;
    gap: 8px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 8);
    }
  }
  &__installment {
    align-items: center;
    display: grid;
    gap: 12px;
    grid-template-columns: 12px auto;
    margin: 0 auto;
    width: fit-content;
    @include media-breakpoint-up(sm) {
      margin-left: 0;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 12);
      grid-template-columns: px-to-vw(12) auto;
    }
    &-icon {
      height: auto;
      width: 100%;
    }
    &--with-tooltip {
      grid-template-columns: 12px auto 12px;
      @include media-breakpoint-up(xl) {
        grid-template-columns: px-to-vw(12) auto px-to-vw(12);
      }
    }
    &-tooltip {
      margin-left: -2px;
      height: auto;
      width: 100%;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(margin-left, -2);
      }
    }
  }
  &__buttons {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: auto;
    @include media-breakpoint-up(sm) {
      align-items: flex-end;
    }
    @include media-breakpoint-up(md) {
      flex-direction: row;
      gap: 16px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 16);
    }
  }
}
</style>
